import { object, string, boolean, mixed } from 'yup';

const regexes = {
  phone: /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/,
  phoneInternational: /^\+\d{10,12}$/,
  email:
    // eslint-disable-next-line no-useless-escape
    /^[a-zA-Zа-яА-ЯёЁ0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Zа-яА-ЯёЁ0-9](?:[a-zA-Zа-яА-ЯёЁ0-9-]{0,61}[a-zA-Zа-яА-ЯёЁ0-9])?(?:\.[a-zA-Zа-яА-ЯёЁ0-9](?:[a-zA-Zа-яА-ЯёЁ0-9-]{0,61}[a-zA-Zа-яА-ЯёЁ0-9])?)+$/, // modified from yup
  cyrillic: /^[а-яё\s-’]+$/im,
};

const translations = {
  required: (t, key) =>
    t('form.errors.required', { fieldName: t(`form.fields.${key}`) }),
  min: (t, key, limit) =>
    t('form.errors.min', { fieldName: t(`form.fields.${key}`), limit }),
  max: (t, key, limit) =>
    t('form.errors.max', { fieldName: t(`form.fields.${key}`), limit }),
  cyrillic: (t, key) =>
    t('form.errors.cyrillic', { fieldName: t(`form.fields.${key}`) }),
  email: (t) => t('form.errors.email'),
  captcha: (t) => t('form.errors.captcha'),
  fileType: (t) => t('form.errors.fileType'),
  fileMaxSize: (t, maxSizeMb) => t('form.errors.fileMaxSize', { maxSizeMb }),
  phone: (t) => t('form.errors.phone'),
};

const fields = {
  name: (t, key = 'name', min = 2, max = 100) =>
    string()
      .required(translations.required(t, key))
      .min(min, translations.min(t, key, min))
      .max(max, translations.max(t, key, max))
      .matches(regexes.cyrillic, translations.cyrillic(t, key)),
  nameOptional: (t, key = 'name', min = 2, max = 100) =>
    string().when(([value], schema) => {
      if (!value?.length) {
        return schema.notRequired();
      } else {
        return schema
          .min(min, translations.min(t, key, min))
          .max(max, translations.max(t, key, max))
          .matches(regexes.cyrillic, translations.cyrillic(t, key));
      }
    }),
  company: (t, key = 'company', max = 300) =>
    string()
      .required(translations.required(t, key))
      .max(max, translations.max(t, key, max)),
  companyOptional: (t, key = 'company', max = 300) =>
    string().when(([value], schema) => {
      if (!value?.length) {
        return schema.notRequired();
      } else {
        return schema.max(max, translations.max(t, key, max));
      }
    }),
  select: (t, key) => string().required(translations.required(t, key)),
  email: (t, key = 'email') =>
    string()
      .required(translations.required(t, key))
      .matches(regexes.email, translations.email(t))
      .max(300, translations.max(t, key, 300)),
  message: (t, key = 'message') =>
    string().required(translations.required(t, key)),
  messageOptional: () => string().notRequired(),
  agreement: boolean().isTrue(),
  captcha: (t) => string().required(translations.captcha(t)),
  phone: (t, key = 'phone') =>
    string()
      .required(translations.required(t, key))
      .matches(regexes.phone, translations.phone(t)),
  phoneInternational: (t, key = 'phone') =>
    string()
      .required(translations.required(t, key))
      .matches(regexes.phoneInternational, translations.phone(t)),
  file: (t, key, types, maxSizeMb) =>
    mixed()
      .required(translations.required(t, key))
      .test('is-valid-type', translations.fileType(t), (file) => {
        const fileExtension = file?.name.toLowerCase().split('.').pop();

        return types.includes(fileExtension);
      })
      .test('is-valid-size', translations.fileMaxSize(t, maxSizeMb), (file) => {
        return file?.size <= maxSizeMb * 1000 * 1024;
      }),
};

export const feedbackFormValidationSchema = () => {
  const { t } = useI18n();

  return object({
    name: fields.name(t),
    email: fields.email(t),
    message: fields.message(t),
    agreement: fields.agreement,
  });
};

export const eventFormValidationSchema = () => {
  const { t } = useI18n();

  return object({
    firstName: fields.name(t, 'firstName'),
    lastName: fields.name(t, 'lastName'),
    fatherName: fields.nameOptional(t, 'fatherName'),
    company: fields.company(t),
    department: fields.companyOptional(t, 'department'),
    position: fields.companyOptional(t, 'position'),
    directionId: fields.select(t, 'direction'),
    email: fields.email(t),
    agreement: fields.agreement,
    captcha: fields.captcha(t),
  });
};

export const vacancyFormValidationSchema = () => {
  const { t } = useI18n();

  return object({
    name: fields.name(t, 'fio'),
    city: fields.company(t, 'city'),
    phone: fields.phone(t),
    email: fields.email(t),
    message: fields.messageOptional(),
    attachment: fields.file(
      t,
      'resume',
      ['doc', 'pdf', 'txt', 'xls', 'docx', 'xlsx'],
      20,
    ),
    agreement: fields.agreement,
    captcha: fields.captcha(t),
  });
};

export const referralFormValidationSchema = () => {
  const { t } = useI18n();

  return object({
    name: fields.name(t, 'fio'),
    city: fields.name(t, 'city'),
    phone: fields.phone(t),
    email: fields.email(t),
    message: fields.messageOptional(),
    referrer: fields.name(t, 'referrerFio'),
    attachment: fields.file(
      t,
      'resume',
      ['doc', 'pdf', 'txt', 'xls', 'docx', 'xlsx'],
      20,
    ),
    agreement: fields.agreement,
    captcha: fields.captcha(t),
  });
};

export const conferenceFormValidationSchema = () => {
  const { t } = useI18n();

  return object({
    firstName: fields.name(t, 'firstName'),
    lastName: fields.name(t, 'lastName'),
    phone: fields.phoneInternational(t),
    email: fields.email(t),
    company: fields.company(t, 'companySimple'),
    position: fields.company(t, 'companyPosition'),
    agreement: fields.agreement,
  });
};
